<template>
  <div v-if="content && hub">
    <ClientOnly>
      <AcceptTermsModal v-if="displayTermsModal" :hub="hub" />
    </ClientOnly>

    <HubHeader :breadcrumbs-items="breadcrumbsItems">
      <template v-if="slots.breadcrumbs" #breadcrumbs>
        <slot name="breadcrumbs" :content-title="content.title" />
      </template>

      <SendContentModal
        v-if="!hub.isContentSendingDeactivated && !isInIframe"
        :content="content"
        :hub-slug="hubSlug"
        :content-slug="contentSlug"
      />
    </HubHeader>

    <SearchReturnPill class="mt-4" />

    <div class="flex items-center mt-5">
      <ContentTypeIcon :content-type="content.type" />
      <span class="text-on-background">
        {{ ContentTypeLabelMap[content.type] }}
      </span>
    </div>

    <div class="flex justify-between flex-wrap gap-y-4 gap-x-20">
      <div>
        <h1 class="font-bold text-3xl text-on-background">
          {{ content.title }}
        </h1>
        <div class="text-on-background-variant">
          {{ content.description }}
        </div>
      </div>
      <div v-if="showLaunchHubOptions" class="w-full md:w-fit">
        <Button
          v-if="content.launchData?.completedAt"
          :label="$t('launch-content-navigation-banner.completed')"
          :right-icon-name="IconName.CHECK"
          :variant="ButtonVariant.OUTLINED"
          :color="ButtonColor.SUCCESS"
          disabled
          class="whitespace-nowrap w-full md:w-fit"
        />
        <CompleteContentButton
          v-else-if="content.type === ContentType.RESOURCE"
          :content="content"
          :hub-slug="hubSlug"
          @complete="redirectOnComplete"
        />
      </div>
    </div>
    <div class="mt-5">
      <update-personalization-settings-banner
        v-if="isSomeContentCustomizable"
      />
      <LaunchContentNavigation
        v-if="showLaunchHubOptions"
        class="mt-5"
        :hub-slug="hubSlug"
        :content="content"
      />
      <ResourceArea
        v-if="content.type === ContentType.RESOURCE && content.resource"
        :resource="content.resource"
        :hub-slug="hubSlug"
        :content-slug="contentSlug"
      />
      <ScormCourseArea
        v-else-if="
          content.type === ContentType.SCORM_COURSE && content.scormCourse
        "
        :hub="hub"
        :content="content"
        :scorm-course="content.scormCourse"
        @refresh="refreshData"
      />
      <SocialsArea
        v-else-if="content.type === ContentType.SOCIALS"
        :socials="filteredSocials"
        :hub="hub"
        :content="content"
        :display-post-later-and-edit="hub.displayPostLaterAndEdit"
        @complete="redirectOnComplete"
      />
      <FilesArea
        v-else-if="content.type === ContentType.FILES"
        :content="content"
        :hub="hub"
        @complete="redirectOnComplete"
      />
    </div>

    <LaunchHubCompletionModal
      v-if="isLaunchHubCompletionModalOpened"
      :hub="hub"
    />
  </div>
</template>

<script setup lang="ts">
import { SearchReturnPill } from '~/pages/_components/search-return-pill';
import { useRoute, useAsyncData, useRouter } from 'nuxt/app';
import { useI18n } from 'vue-i18n';
import { ContentTypeIcon } from '~/components/content-type-icon';
import {
  apiGetContentsList,
  apiGetSingleContent,
  ContentType,
  useContentTypeLabelMap,
} from '~/api/contents';
import { computed, useOrganizationDomain } from '#imports';
import { ResourceArea } from './_components/resource-area';
import { ScormCourseArea } from './_components/scorm-course-area';
import { SocialsArea } from './_components/socials-area';
import { FilesArea } from './_components/files-area';
import { SendContentModal } from './_components/send-content-modal';
import { AcceptTermsModal } from '~/pages/_components/hub-details/_components/accept-terms-modal';
import { useAcceptedHubsTerms } from '~/pages/_components/hub-details/_composables/use-accepted-hubs-terms';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import HubHeader from '~/pages/_components/hub-header/hub-header.vue';
import { handleApiError } from '~/api';
import { UpdatePersonalizationSettingsBanner } from '~/pages/_components/content-details/_components/update-personalization-settings-banner';
import { LaunchContentNavigation } from '~/pages/_components/content-details/_components/launch-content-navigation';
import { CompleteContentButton } from '~/pages/_components/content-details/_components/complete-content-button';
import { createContentLaunchMetric, LaunchMetricType } from '~/api/metrics';
import { captureHandledError } from '~/constants/sentry';
import { useAccountStore } from '~/store/account';
import { AccountType } from '~/api/accounts';
import { useAuthUserStore } from '~/store/auth-user';
import { useHubStore } from '~/store/hub';
import { LaunchHubCompletionModal } from './_components/launch-hub-completion-modal';
import { Button, ButtonColor, ButtonVariant, IconName } from '@exfluential/ui';
import { useIsInIframe } from '~/composables/use-is-in-iframe';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const props = defineProps<{
  hubSlug: string;
  contentSlug: string;
}>();

const slots = defineSlots<{
  breadcrumbs(props: { contentTitle: string }): unknown;
}>();

const hubSlug = computed(() => props.hubSlug);
const contentSlug = computed(() => props.contentSlug);

const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const isInIframe = useIsInIframe();
const { currentAccount } = storeToRefs(useAccountStore());
const { authUser } = storeToRefs(useAuthUserStore());
const hubStore = useHubStore();
const { refreshHub } = hubStore;
const { hub } = storeToRefs(hubStore);

const isLaunchHubCompletionModalOpened = ref(false);

watch(
  hub,
  () => {
    if (
      hub.value?.isEveryContentCompleted &&
      hub.value.launchHubCompletionModalDisplayedAt === null &&
      currentAccount.value?.role === AccountType.STANDARD
    ) {
      isLaunchHubCompletionModalOpened.value = true;
    }
  },
  { immediate: true },
);

const ContentTypeLabelMap = useContentTypeLabelMap();

const {
  data: content,
  error: contentError,
  refresh,
} = await useAsyncData(`content-${hubSlug.value}-${contentSlug.value}`, () => {
  return apiGetSingleContent(hubSlug.value, contentSlug.value, {
    includeNextPrevSlugs: true,
  });
});

if (contentError.value) {
  await handleApiError(contentError.value);
}

const refreshData = async () => {
  await Promise.all([refreshHub(), refresh()]);
};

if (
  hub.value?.isLaunchEnabled &&
  currentAccount.value?.type === AccountType.STANDARD &&
  content.value &&
  !content.value.launchData?.openedAt
) {
  try {
    await createContentLaunchMetric(
      hub.value.slug,
      content.value.slug,
      LaunchMetricType.OPENED,
    );
  } catch (err) {
    captureHandledError(err, { feature: 'launch-metrics' });
  }
}

useHead({
  title: content.value?.title || organization.value?.name,
});

const { areHubTermsAccepted } = useAcceptedHubsTerms();

const redirectOnComplete = () =>
  useLoader({
    action: async () => {
      const nextNotCompletedContent = await apiGetContentsList(hubSlug.value, {
        page: 1,
        perPage: 1,
        launchMetricTypes: [
          LaunchMetricType.NOT_STARTED,
          LaunchMetricType.OPENED,
        ],
        sortDir: 'asc',
      });
      if (nextNotCompletedContent.totalCount) {
        router.push(
          `/${hubSlug.value}/${nextNotCompletedContent.nodes[0].slug}`,
        );
      } else {
        isLaunchHubCompletionModalOpened.value = true;
      }
    },
  });

const showLaunchHubOptions = computed(
  () =>
    currentAccount.value?.type === AccountType.STANDARD &&
    hub.value?.isLaunchEnabled,
);

const displayTermsModal = computed(() => {
  return (
    process.client &&
    hub.value?.displayTerms &&
    !areHubTermsAccepted(hub.value.id)
  );
});

const organizationDomain = useOrganizationDomain();

/* Temporary exclusion of twitter from social posts until customer's decision to use paid API */
const filteredSocials = computed(() => {
  return (
    content.value?.socials?.filter((social) => social.type !== 'twitter') ?? []
  );
});

const isSomeContentCustomizable = computed(() =>
  content.value?.socials?.some((social) => social.isCustomizable),
);

const breadcrumbsItems = computed(() => {
  if (!hub.value || !content.value) {
    return [];
  }

  const org = organizationDomain === null ? organization.value : null;
  const prefix = org ? `/org/${org.slug}` : '';

  return [
    { label: t('common.dashboard'), to: '/' },
    ...(org ? [{ label: org.name, to: `/org/${org.slug}` }] : []),
    { label: hub.value.title, to: `${prefix}/${hub.value.slug}` },
    {
      label: content.value.title,
      to: `${prefix}/${hub.value.slug}/${content.value.slug}`,
    },
  ];
});
</script>
